import React, { useState } from 'react';
import AssignUsersModal from './AssignUsersModal';

/**
 * PipelineCard — compact Kanban card with SLA time tracking.
 */
const PipelineCard = ({ card, isDragging, slaGreenHours = 24, slaYellowHours = 48, onReload }) => {
  const [imgError, setImgError] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const showImg = card.avatar_url && !imgError;
  const initials = card.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

  // SLA color based on entered_stage_at
  const getSlaColor = () => {
    if (!card.entered_stage_at) return '';
    const hoursInStage = (Date.now() - new Date(card.entered_stage_at).getTime()) / (1000 * 60 * 60);
    if (hoursInStage <= slaGreenHours) return 'bg-green-50 border-l-green-400';
    if (hoursInStage <= slaYellowHours) return 'bg-yellow-50 border-l-yellow-400';
    return 'bg-red-50 border-l-red-400';
  };

  const priorityStyles = {
    high: { badge: 'bg-red-50 text-red-700', label: 'High' },
    medium: { badge: 'bg-yellow-50 text-yellow-700', label: 'Med' },
    low: { badge: 'bg-green-50 text-green-700', label: 'Low' },
  };
  const ps = priorityStyles[card.priority] || null;
  const slaClass = getSlaColor();

  return (
    <div className={`rounded-lg px-2.5 py-2 cursor-pointer hover:shadow-md transition-shadow border border-l-[3px] ${
      isDragging ? 'shadow-lg ring-2 ring-blue-200' : ''
    } ${slaClass || 'bg-white border-l-gray-200'}`}>
      <div className="flex items-center gap-2">
        {showImg ? (
          <img src={card.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0 border border-gray-200" onError={() => setImgError(true)} />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
            {initials}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-1">
            <p className="text-xs font-medium text-gray-900 truncate">{card.name}</p>
            <div className="flex items-center gap-1 flex-shrink-0">
              {card.creator_user_type === 'client' && <span className="text-[8px] font-medium px-1 py-0.5 rounded bg-teal-50 text-teal-600 border border-teal-200">Client</span>}
              {ps && <span className={`text-[9px] font-semibold px-1 py-0.5 rounded ${ps.badge}`}>{ps.label}</span>}
            </div>
          </div>
          {(card.current_title || card.current_company) && (
            <p className="text-[10px] text-gray-500 truncate mt-0.5">
              {card.current_title}{card.current_company ? ` · ${card.current_company}` : ''}
            </p>
          )}
          <div className="flex items-center justify-between mt-1">
            <div className="flex items-center gap-1">
              {card.assigned_users?.length > 0 && (
                <div className="flex -space-x-1">
                  {card.assigned_users.slice(0, 3).map((u, i) => (
                    <div key={i} className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-[7px] font-bold flex items-center justify-center border border-white" title={u.name}>
                      {u.name?.charAt(0)?.toUpperCase()}
                    </div>
                  ))}
                  {card.assigned_users.length > 3 && (
                    <div className="w-4 h-4 rounded-full bg-gray-100 text-gray-500 text-[7px] font-bold flex items-center justify-center border border-white">
                      +{card.assigned_users.length - 3}
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); setShowAssign(true); }}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                className="text-[8px] px-1.5 py-0.5 text-blue-600 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 font-medium">
                {card.assigned_users?.length > 0 ? '+' : 'Assign'}
              </button>
            </div>
            {card.entered_stage_at && (
              <span className="text-[9px] text-gray-400">
                {(() => {
                  const hrs = Math.floor((Date.now() - new Date(card.entered_stage_at).getTime()) / (1000 * 60 * 60));
                  if (hrs < 1) return '<1h';
                  if (hrs < 24) return `${hrs}h`;
                  const days = Math.floor(hrs / 24);
                  return `${days}d`;
                })()}
              </span>
            )}
          </div>
        </div>
      </div>
      {showAssign && (
        <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          <AssignUsersModal cardId={card.id} currentAssignments={card.assigned_users || []}
            onClose={() => setShowAssign(false)} onUpdate={() => { setShowAssign(false); onReload?.(); }} />
        </div>
      )}
    </div>
  );
};

export default PipelineCard;
